import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { FinanceApiService, ReconciliationCaseRecord } from './finance-api.service';

@Component({
  standalone: true,
  selector: 'ain-finance-reconciliation',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, InputTextModule],
  providers: [MessageService],
  template: `
    <ain-page title="Reconciliation Queue" description="Review, triage, and approve reconciliation adjustments">
      @if (!capabilityEnabled()) {
        <ain-state type="empty" title="Reconciliation capability is disabled" message="Enable financial-core.reconciliation in control center." />
      } @else {
      <div class="toolbar">
        <input pInputText [(ngModel)]="ownerUserId" placeholder="Owner user id for triage" />
        <button pButton type="button" label="Refresh" (click)="load()"></button>
      </div>

      <p-table [value]="cases()">
        <ng-template pTemplate="header">
          <tr><th>Type</th><th>Source</th><th>Target</th><th>Difference</th><th>Status</th><th>Actions</th></tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td>{{ row.reconciliationType }}</td>
            <td>{{ row.sourceReference }}</td>
            <td>{{ row.targetReference }}</td>
            <td>{{ row.differenceAmount }}</td>
            <td>{{ row.status }}</td>
            <td class="actions">
              <button pButton type="button" size="small" label="Triage" (click)="triage(row.id)"></button>
              <button pButton type="button" size="small" severity="secondary" label="Approve" (click)="approve(row.id)"></button>
            </td>
          </tr>
        </ng-template>
      </p-table>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; margin-bottom: 12px; }
    .actions { display: flex; gap: 6px; }
  `]
})
export class FinanceReconciliationComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly messages = inject(MessageService);

  readonly cases = signal<ReconciliationCaseRecord[]>([]);
  readonly capabilityEnabled = signal(false);
  ownerUserId = '';

  ngOnInit(): void {
    this.api.getCapabilitySetting('financial-core.reconciliation').subscribe({
      next: setting => {
        this.capabilityEnabled.set(setting.isEnabled);
        if (setting.isEnabled) this.load();
      }
    });
  }

  load(): void {
    this.api.listReconciliationCases().subscribe({ next: rows => this.cases.set(rows) });
  }

  triage(caseId: string): void {
    const owner = this.ownerUserId.trim();
    if (!owner) {
      this.messages.add({ severity: 'warn', summary: 'Owner user id is required' });
      return;
    }

    this.api.triageReconciliationCase(caseId, owner).subscribe({
      next: () => this.load(),
      error: () => this.messages.add({ severity: 'error', summary: 'Triage failed' })
    });
  }

  approve(caseId: string): void {
    this.api.approveReconciliationCase(caseId).subscribe({
      next: () => this.load(),
      error: () => this.messages.add({ severity: 'error', summary: 'Approve adjustment failed' })
    });
  }
}
